/**
 * CR-21520: Web Worker–based prefetch hook for certification details infinite scroll.
 *
 * Offloads prefetch API calls and data processing to a background Web Worker thread,
 * keeping the main UI thread responsive. The hook manages the worker lifecycle,
 * buffer state, and message passing between the component and the worker.
 *
 * Flow:
 *  1. Component calls triggerPrefetch(offset, totalItems) after initial/fresh load
 *  2. Hook sends PREFETCH message to worker with auth token + request body
 *  3. Worker makes fetch() calls and processes responses off the main thread
 *  4. Worker posts PREFETCH_RESULT back — hook stores results in local buffer
 *  5. Component calls consumeFromBuffer() on scroll → returns 'served'/'pending'/'empty'
 */
import { useCallback, useEffect, useRef } from 'react';
import { Cookies } from 'react-cookie';
import { useSelector } from 'react-redux';

import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_ROLEOWNER,
} from '../utils/constant';
import { checkEnvironment, getCertsMSconfigFromStore } from '../utils/utils';

const cookies = new Cookies();

/**
 * Returns the correct base URL for the items API.
 * Mirrors getBaseURL logic from http-service/utils.js for the items endpoint.
 */
function getWorkerBaseURL() {
  const certsMSconfig = getCertsMSconfigFromStore();
  const { GATEWAY_API_URL, ECM_URL } = checkEnvironment();

  // /certifications/v2/items is NOT in the exclusion list, so it uses CertMS
  if (Number(certsMSconfig) === 1) {
    return `${GATEWAY_API_URL}/api/certms`;
  }

  return ECM_URL;
}

/**
 * Builds the request body for prefetch API calls from current Redux state.
 * Extracted so the main thread prepares serializable data for the worker.
 */
function buildPrefetchRequestBody(
  certDetailsState,
  certKey,
  columnNames,
  searchText
) {
  const {
    core: { campaignType },
    tabs: { tabData },
    viewControls: {
      groupByOptions,
      dynamicSortFilter,
      advancedFilters,
      appliedRowFilters,
      smartFilterId,
      smartFiltersMeta,
      isSmartApplied,
      appliedSaviActionKeys,
    },
  } = certDetailsState;

  // Must match tabMapping from redux/certdetails/types.js
  const tabMapping = {
    TAB_STATUS_TODO: 'todo',
    TAB_STATUS_DONE: 'done',
    TAB_STATUS_ALL: 'all',
    TAB_STATUS_CONS: 'consulted',
  };

  const activeTabKey = tabData?.find((tab) => tab.isActive)?.key;
  const activeTab = tabMapping[activeTabKey] || 'all';
  const { sortByColumn, sortDirection } = dynamicSortFilter;
  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isROCampaign = CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ROLEOWNER;

  const activeSmartFilter =
    isSmartApplied && smartFilterId
      ? smartFiltersMeta?.risk_factors?.factors?.find(
          (f) => f.id === smartFilterId
        )?.payload
      : null;

  const saviActionFilters = (appliedSaviActionKeys || []).map((key) => ({
    key: 'SAVI_ACTION',
    value: key,
    isChecked: '1',
  }));

  const groupedAdvancedFilters = (advancedFilters || [])
    .filter((f) => f.isChecked)
    .reduce((acc, f) => {
      const k = f.key || f.id;

      acc[k] = acc[k] ? [...acc[k], f.value] : [f.value];

      return acc;
    }, {});

  saviActionFilters.forEach((f) => {
    groupedAdvancedFilters[f.key] = groupedAdvancedFilters[f.key]
      ? [...groupedAdvancedFilters[f.key], f.value]
      : [f.value];
  });

  const body = {
    certkey: certKey,
    requestOrigin: 'UI',
    sSearch: `${searchText}`,
    columnsToShow: columnNames,
    tab: activeTab,
    group_by: selectedGroupByValue,
    filters: {
      rowsFilters: appliedRowFilters || [],
      advancedFilters: activeSmartFilter ? {} : groupedAdvancedFilters,
      ...(activeSmartFilter && {
        smartFilters: { ...activeSmartFilter, checked: '1' },
      }),
    },
  };

  if (!isEOCampaign) {
    body.group_by = selectedGroupByValue;
  }

  if (sortByColumn && sortDirection) {
    body.columnNameToSort = sortByColumn;
    body.order = sortDirection;
  }

  const campaignConfig = { isEOCampaign, isROCampaign };

  return { requestBody: body, campaignConfig };
}

/**
 * Hook that manages a Web Worker for background prefetching of cert details.
 *
 * @param {Object} params
 * @param {string} params.certKey - The certification key
 * @param {string} params.columnNames - Column names to fetch
 * @param {string} params.searchText - Current search text
 * @param {number} params.batchSize - Number of items per batch
 * @param {Function} params.onBatchReady - Called with (results[]) when prefetched data is available
 *
 * @returns {{ triggerPrefetch, consumeFromBuffer, resetPrefetch, bufferLength }}
 */
export default function usePrefetchWorker({
  certKey,
  columnNames,
  searchText,
  batchSize,
  onBatchReady,
  onPendingServed,
}) {
  const workerRef = useRef(null);
  const bufferRef = useRef([]);
  const prefetchOffsetRef = useRef(0);
  const isPrefetchingRef = useRef(false);
  const pendingConsumeRef = useRef(false);
  const requestIdRef = useRef(0);
  const totalItemsRef = useRef(0);
  const onBatchReadyRef = useRef(onBatchReady);
  const onPendingServedRef = useRef(onPendingServed);
  const batchSizeRef = useRef(batchSize);
  /** Ref to access triggerPrefetchInternal inside worker onmessage without stale closures */
  const triggerPrefetchRef = useRef(null);

  // Select only cert details state to avoid re-creating callbacks on every unrelated Redux change
  const certDetailsState = useSelector(
    (state) => state?.certifications?.details
  );

  // Keep refs in sync
  useEffect(() => {
    onBatchReadyRef.current = onBatchReady;
  }, [onBatchReady]);

  useEffect(() => {
    onPendingServedRef.current = onPendingServed;
  }, [onPendingServed]);

  useEffect(() => {
    batchSizeRef.current = batchSize;
  }, [batchSize]);

  /**
   * Internal: sends prefetch message to worker.
   * Only sends batches whose offset < totalItems — stops when offset >= total.
   */
  const triggerPrefetchInternal = useCallback(
    (startOffset) => {
      const total = totalItemsRef.current;

      if (
        isPrefetchingRef.current ||
        !workerRef.current ||
        !columnNames ||
        startOffset >= total
      ) {
        return;
      }

      const bs = batchSizeRef.current;
      const batches = [
        { max: bs, offset: startOffset },
        { max: bs, offset: startOffset + bs },
      ].filter((b) => b.offset < total);

      if (batches.length === 0) return;

      isPrefetchingRef.current = true;
      requestIdRef.current += 1;

      const baseURL = getWorkerBaseURL();
      const token = cookies.get('access_token');
      const { requestBody, campaignConfig } = buildPrefetchRequestBody(
        certDetailsState,
        certKey,
        columnNames,
        searchText
      );

      workerRef.current.postMessage({
        type: 'PREFETCH',
        id: requestIdRef.current,
        payload: { baseURL, token, batches, requestBody, campaignConfig },
      });
    },
    [certKey, columnNames, searchText, certDetailsState]
  );

  useEffect(() => {
    triggerPrefetchRef.current = triggerPrefetchInternal;
  }, [triggerPrefetchInternal]);

  // Initialize worker
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('./prefetch.worker.js', import.meta.url),
      { type: 'module' }
    );

    workerRef.current.onmessage = (event) => {
      const { type, id, results } = event.data;

      if (type === 'PREFETCH_RESULT') {
        // Ignore stale results from an outdated prefetch request
        if (id !== requestIdRef.current) return;

        isPrefetchingRef.current = false;

        // Store valid results in buffer
        results?.forEach((result) => {
          if (result?.results) {
            bufferRef.current.push(result.results);
          }
        });

        // Update prefetch offset
        const validResults = results?.filter((r) => r?.results) || [];

        prefetchOffsetRef.current += validResults.length * batchSizeRef.current;

        // If a consume was waiting, serve now
        if (pendingConsumeRef.current && bufferRef.current.length > 0) {
          pendingConsumeRef.current = false;

          const nextBatch = bufferRef.current.shift();

          onBatchReadyRef.current(nextBatch);
          onPendingServedRef.current?.();
        }

        // Prefetch completed but nothing was pending — clear loading state
        if (pendingConsumeRef.current && bufferRef.current.length === 0) {
          pendingConsumeRef.current = false;
          onPendingServedRef.current?.();
        }

        // Eagerly trigger next prefetch if buffer is running low (≤ 1 batch),
        // so the next set of data is ready before the user scrolls through
        // the remaining buffer. Uses ref to avoid stale closure.
        if (bufferRef.current.length <= 1) {
          triggerPrefetchRef.current?.(prefetchOffsetRef.current);
        }
      }

      if (type === 'PREFETCH_ERROR') {
        // Ignore stale errors from an outdated prefetch request
        if (id !== requestIdRef.current) return;

        isPrefetchingRef.current = false;

        // Clear pending state and loader on error
        if (pendingConsumeRef.current) {
          pendingConsumeRef.current = false;
          onPendingServedRef.current?.();
        }
      }
    };

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  /**
   * Triggers prefetch from a given offset. Called externally after initial/fresh load.
   * Stores totalItems so internal calls (from consumeFromBuffer) also respect the limit.
   */
  const triggerPrefetch = useCallback(
    (startOffset, totalItems) => {
      totalItemsRef.current = totalItems;

      if (startOffset >= totalItems) return;

      prefetchOffsetRef.current = startOffset;
      triggerPrefetchInternal(startOffset);
    },
    [triggerPrefetchInternal]
  );

  /**
   * Consumes next batch from buffer.
   * Returns: 'served' (data shown), 'pending' (prefetch in-flight, will auto-serve),
   *          'empty' (nothing available, caller should call API).
   */
  const consumeFromBuffer = useCallback(() => {
    // Buffer has data — serve instantly
    if (bufferRef.current.length > 0) {
      const nextBatch = bufferRef.current.shift();

      onBatchReadyRef.current(nextBatch);

      return 'served';
    }

    // Prefetch in-flight — mark pending for auto-serve when it completes
    if (isPrefetchingRef.current) {
      pendingConsumeRef.current = true;

      return 'pending';
    }

    // Nothing available — caller should fall back to direct API call
    return 'empty';
  }, []);

  /**
   * Resets all prefetch state. Call on tab switch, filter, sort, etc.
   * Bumps requestId so any in-flight worker responses are discarded as stale.
   */
  const resetPrefetch = useCallback(() => {
    bufferRef.current = [];
    prefetchOffsetRef.current = 0;
    isPrefetchingRef.current = false;
    pendingConsumeRef.current = false;
    requestIdRef.current += 1;

    workerRef.current?.postMessage({ type: 'RESET' });
  }, []);

  return {
    triggerPrefetch,
    consumeFromBuffer,
    resetPrefetch,
    getBufferLength: () => bufferRef.current.length,
    getPrefetchOffset: () => prefetchOffsetRef.current,
    setPrefetchOffset: (val) => {
      prefetchOffsetRef.current = val;
    },
  };
}
